import { useDialog } from '@/common/dialog/DialogContext';
import { EntityBulkGridDialog } from '@/common/entityFramework/bulk/EntityBulkGridDialog';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, IconButton, Stack, Tooltip } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { ProgContextStrip } from '../prog/ProgContextStrip';
import { PCT_CALC_TYPES } from '../progProd/progProdCalcTypeUtils';
import { TierFormulaBuilderDialog } from './formFields/TierFormulaBuilderDialog';
import { validateFormula } from './formFields/TierFormulaUtils';
import { useTierFormulaPalette } from './formFields/useTierFormulaPalette';

const FormulaCellRenderer = (props) => {
  const { openDialog } = useDialog();
  const column = props.colDef.bulkColumn;

  const openBuilder = (event) => {
    event.preventDefault();
    event.stopPropagation();
    openDialog(TierFormulaBuilderDialog, {
      data: {
        formula: props.value ?? '',
        palette: column.palette,
      },
    }).then((result) => {
      if (!result) return;
      props.node.setDataValue(column.field, result.formula);
    });
  };

  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ height: '100%', minWidth: 0 }}>
      <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{props.value}</Box>
      <Tooltip title="Open formula builder">
        <IconButton size="small" onMouseDown={openBuilder}>
          <FontAwesomeIcon icon={fas.faPencil} />
        </IconButton>
      </Tooltip>
    </Stack>
  );
};

// Column headers mirror the PROG_PROD_TIER sheet of the Contract Excel upload.
// ownerProgProd pins every row to one Program Product (per-card entry); otherwise prog_prod_code is a pasted column.
// preloadExisting starts the grid from the owner's current tiers so the same dialog serves as the tier set editor.
export const TierBulkDialog = ({
  closeDialog,
  progObj,
  progProds = [],
  existingTiers = [],
  ownerProgProd,
  preloadExisting = false,
}) => {
  const rsrc = useRsrc();
  const [fieldDefs, setFieldDefs] = useState(null);
  const palette = useTierFormulaPalette(progObj.id);

  useEffect(() => {
    rsrc.fieldDefs('md', 'PROG_PROD_TIER').then((defs) => {
      setFieldDefs(Object.fromEntries(defs.map((d) => [d.field, d])));
    });
  }, [progObj.id]);

  const progProdIdToCode = useMemo(() => Object.fromEntries(progProds.map((pp) => [pp.id, pp.code])), [progProds]);

  const progProdCodeToProdCode = useMemo(
    () => Object.fromEntries(progProds.map((pp) => [pp.code, pp.prodCode])),
    [progProds],
  );

  const columns = useMemo(() => {
    if (!fieldDefs) return null;
    return [
      {
        field: 'progProdCode',
        header: 'prog_prod_code',
        displayName: 'Program Product',
        type: 'entity',
        module: 'md',
        entity: 'PROG_PROD',
        expr: ExprUtils.whereParams('progId', progObj.id),
        required: true,
        fixed: Boolean(ownerProgProd),
      },
      { field: 'tier', header: 'tier', displayName: 'Tier #', type: 'number', required: true, width: 90 },
      {
        field: 'formula',
        header: 'formula',
        displayName: 'Formula',
        type: 'text',
        required: true,
        width: 420,
        cellRenderer: FormulaCellRenderer,
        palette,
        progProdCodeToProdCode,
      },
      {
        field: 'progProdCalcType',
        header: 'prog_prod_calc_type',
        displayName: 'Calc Type',
        type: 'enum',
        values: fieldDefs.progProdCalcType?.values ?? [],
      },
      {
        field: 'pymtBasis',
        header: 'pymt_basis',
        displayName: 'Payment Basis',
        type: 'enum',
        values: fieldDefs.pymtBasisId?.values ?? [],
      },
      {
        field: 'disc',
        header: 'disc',
        displayName: 'Discount',
        type: 'number',
        typeForRow: (row) => (PCT_CALC_TYPES.includes(row?.progProdCalcType) ? 'percent' : 'number'),
        typeDependsOn: ['progProdCalcType'],
        width: 110,
      },
    ];
  }, [fieldDefs, ownerProgProd, progObj.id, progProdCodeToProdCode, palette]);

  const existingKeys = useMemo(
    () =>
      new Set(existingTiers.map((tier) => `${progProdIdToCode[tier.progProdId] ?? tier.progProdCode}|${tier.tier}`)),
    [existingTiers, progProdIdToCode],
  );

  const classifyRow = (row) => (existingKeys.has(`${row.progProdCode}|${row.tier}`) ? 'UPDATE' : 'NEW');

  const validateRow = (row) => {
    const errors = {};
    if (row.tier != null && (!Number.isInteger(row.tier) || row.tier < 0))
      errors.tier = 'Must be a whole number (0 or greater)';
    const formulaError = validateFormula(row.formula);
    if (formulaError) errors.formula = formulaError;
    return errors;
  };

  const onSubmit = (rows) =>
    rsrc.store('md', 'PROG_BULK_STORE_PLUGIN', { progId: progObj.id, entity: 'PROG_PROD_TIER', rows });

  // Existing tiers in the same layout so users can copy, adjust in Excel, and paste back
  const exportRows = useMemo(
    () =>
      existingTiers
        .filter((tier) => !ownerProgProd || tier.progProdId === ownerProgProd.id)
        .map((tier) => ({
          progProdCode: progProdIdToCode[tier.progProdId] ?? tier.progProdCode,
          tier: tier.tier,
          formula: tier.formula,
          progProdCalcType: tier.progProdCalcType,
          pymtBasis: tier.pymtBasisCode,
          disc: tier.disc,
        })),
    [existingTiers, ownerProgProd, progProdIdToCode],
  );

  if (!columns) return null;

  return (
    <EntityBulkGridDialog
      closeDialog={closeDialog}
      title={
        preloadExisting ? `Edit Tiers — ${ownerProgProd?.code ?? progObj.code}`
        : ownerProgProd ?
          `Paste Tiers — ${ownerProgProd.code}`
        : `Paste Tiers — ${progObj.code}`
      }
      subtitle="A row whose product already has that tier number updates it; other rows add new tiers."
      header={<ProgContextStrip progObj={progObj} />}
      columns={columns}
      fixedValues={ownerProgProd ? { progProdCode: ownerProgProd.code } : {}}
      preloadRows={preloadExisting ? exportRows : []}
      classifyRow={classifyRow}
      validateRow={validateRow}
      onSubmit={onSubmit}
      exportRows={exportRows}
    />
  );
};
